// Sentry wiring (prod-readiness ticket 123). Off unless REACT_APP_SENTRY_DSN
// is set. Strict PII config: no session replay, no tracing, no breadcrumbs,
// no email/name/ip on the user — only { id, tenant_id }. `beforeSend` and the
// scrub helpers are pure/exported so they can be unit-tested without ever
// calling `Sentry.init`.
import * as Sentry from '@sentry/react';

export const SENTRY_ENABLED = !!process.env.REACT_APP_SENTRY_DSN;

// Same shapes as backend/log_redaction.py's redact_text(), scoped down to
// what a browser event can realistically carry (ticket 123 F1).
const SENSITIVE_PARAMS = ['token', 'auth', 'key', 'secret', 'code', 'otp'];
const PARAM_ALT = SENSITIVE_PARAMS.join('|');
const QS_PARAM_RE = new RegExp(`([?&](?:${PARAM_ALT})=)([^&\\s"'\\\\]+)`, 'gi');
// Capture-and-reinsert instead of a lookbehind: `(?<!...)` throws a
// SyntaxError at parse time on Safari < 16.4, and this module is imported
// from index.js, so a lookbehind here would white-screen the whole app on
// those browsers before any of our own code runs.
const BARE_PARAM_RE = new RegExp(`(^|[^\\w-])(${PARAM_ALT})=([^&\\s"'\\\\]+)`, 'gi');
const JWT_RE = /eyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const BEARER_RE = /(bearer\s+)[A-Za-z0-9._~+/=-]{8,}/gi;
const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/g;
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

// Public/tokenized routes carry the secret in the path itself
// (/api/share/<40-char-token>, /portal/upload/<token>) — a bare pathname
// isn't safe by construction. UUID-shaped segments (deal ids, etc.) are fine
// to keep; anything else long enough to be a token gets redacted.
const TOKEN_SEGMENT_RE = /^[A-Za-z0-9_-]{20,}$/;
const UUID_SEGMENT_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// Pure, never throws.
export function scrubPath(path) {
  if (typeof path !== 'string' || !path) return path;
  try {
    return path
      .split('/')
      .map((seg) => (seg && !UUID_SEGMENT_RE.test(seg) && TOKEN_SEGMENT_RE.test(seg) ? '<token>' : seg))
      .join('/');
  } catch {
    return path;
  }
}

function maskSsn(match) {
  const digits = match.replace(/\D/g, '');
  const tail = digits.length >= 4 ? digits.slice(-4) : '****';
  return `***-**-${tail}`;
}

// Pure, never throws — any regex/type surprise returns the input unchanged.
export function scrub(value) {
  if (typeof value !== 'string' || !value) return value;
  try {
    let out = value.replace(QS_PARAM_RE, '$1<redacted>');
    out = out.replace(BARE_PARAM_RE, '$1$2=<redacted>');
    out = out.replace(JWT_RE, '<jwt>');
    out = out.replace(BEARER_RE, '$1<redacted>');
    out = out.replace(SSN_RE, maskSsn);
    out = out.replace(EMAIL_RE, '<email>');
    return out;
  } catch {
    return value;
  }
}

// Pure, never throws — walks objects/arrays and applies scrub() to every
// string leaf. Used for `contexts`, which can carry a non-Error rejection's
// serialized value verbatim.
export function scrubDeep(obj) {
  try {
    if (typeof obj === 'string') return scrub(obj);
    if (Array.isArray(obj)) return obj.map(scrubDeep);
    if (obj && typeof obj === 'object') {
      const out = {};
      for (const k of Object.keys(obj)) out[k] = scrubDeep(obj[k]);
      return out;
    }
    return obj;
  } catch {
    return obj;
  }
}

// Reduce a Sentry user object down to { id, tenant_id } only — never email,
// name, or ip_address.
function scrubUser(user) {
  if (!user || typeof user !== 'object') return undefined;
  const keep = {};
  if (user.id !== undefined) keep.id = user.id;
  if (user.tenant_id !== undefined) keep.tenant_id = user.tenant_id;
  return Object.keys(keep).length ? keep : undefined;
}

function scrubRequest(req) {
  if (!req || typeof req !== 'object') return req;
  const { headers, cookies, data, query_string, ...rest } = req;
  if (typeof rest.url === 'string') {
    try {
      rest.url = scrubPath(new URL(rest.url, window.location.origin).pathname);
    } catch {
      delete rest.url;
    }
  }
  return rest;
}

function scrubExceptionValues(exception) {
  if (!exception || !Array.isArray(exception.values)) return exception;
  return {
    ...exception,
    values: exception.values.map((v) => (v && typeof v.value === 'string' ? { ...v, value: scrub(v.value) } : v)),
  };
}

// Module-level, pure, testable — never raises. On any exception, fail closed
// toward less data by stripping request/breadcrumbs/user rather than letting
// PII through.
export function beforeSend(event) {
  if (!event || typeof event !== 'object') return event;
  try {
    const next = { ...event };
    if ('request' in next) next.request = scrubRequest(next.request);
    if ('user' in next) next.user = scrubUser(next.user);
    if ('exception' in next) next.exception = scrubExceptionValues(next.exception);
    if (typeof next.message === 'string') next.message = scrub(next.message);
    // `contexts` (react componentStack, browser info, etc.) can carry a
    // non-Error rejection's value verbatim — scrub every string in it rather
    // than trying to enumerate the shapes that land there.
    if ('contexts' in next) next.contexts = scrubDeep(next.contexts);
    delete next.breadcrumbs;
    // Non-Error `throw`/rejections get serialized into extra.__serialized__
    // unscrubbed — drop it rather than try to scrub an arbitrary blob.
    delete next.extra;
    return next;
  } catch {
    const { request, breadcrumbs, user, extra, ...rest } = event;
    return rest;
  }
}

export function initSentry() {
  if (!SENTRY_ENABLED) return false;
  try {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      environment: process.env.REACT_APP_APP_ENV || (process.env.NODE_ENV === 'production' ? 'production' : 'development'),
      release: process.env.REACT_APP_GIT_SHA || undefined,
      sendDefaultPii: false,
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // @sentry/react v10 browser defaults: InboundFilters, FunctionToString,
      // ConversationId, BrowserApiErrors, Breadcrumbs, GlobalHandlers,
      // LinkedErrors, Dedupe, HttpContext, CultureContext, BrowserSession.
      // Replay/BrowserTracing are opt-in, not defaults, but are excluded here
      // too in case that ever changes. Breadcrumbs is redundant with
      // maxBreadcrumbs: 0 / beforeBreadcrumb below, but dropping the
      // integration is the belt to that suspenders. BrowserSession sends its
      // own session envelopes (including navigator.userAgent) straight to
      // Sentry — those never pass through beforeSend, so it has to go too.
      integrations: (defaults) => defaults.filter((i) => !['Breadcrumbs', 'BrowserSession', 'Replay', 'BrowserTracing'].includes(i.name)),
      beforeSend,
      beforeBreadcrumb: () => null,
      maxBreadcrumbs: 0,
    });
    return true;
  } catch {
    return false;
  }
}

// No email, no name, no ip — mirrors backend before_send's user shape.
export function setSentryUser(user) {
  if (!SENTRY_ENABLED) return;
  try {
    Sentry.setUser(user ? { id: user.user_id, tenant_id: user.tenant_id } : null);
  } catch {
    // never block the caller on a Sentry hiccup
  }
}

// `ctx` is a full Sentry capture-context object (e.g. { tags }, { contexts }),
// not a bag of tag values — callers pick the right shape for what they're
// attaching (tags are truncated; a component stack belongs in `contexts`).
export function captureException(err, ctx) {
  if (!SENTRY_ENABLED) return null;
  try {
    return Sentry.captureException(err, ctx) || null;
  } catch {
    return null;
  }
}

export function lastEventId() {
  if (!SENTRY_ENABLED) return null;
  try {
    return Sentry.lastEventId() || null;
  } catch {
    return null;
  }
}

// Last X-Request-ID seen on a backend response (ticket 123 F6) — the error
// boundary shows this as a reference when no Sentry event id is known.
export function setLastRequestId(id) {
  if (typeof window !== 'undefined' && id) window.__lastRequestId = id;
}

export function getLastRequestId() {
  return (typeof window !== 'undefined' && window.__lastRequestId) || null;
}
